// PostMedia.tsx — dispatcher for every kind of post media.
//
// Was 236 lines of two unrelated concerns (decorative CSS/SVG ornaments + uploaded images).
// The ornaments now live in media/decorative/, the real images in media/MediaFrame|MediaDeck,
// and this file just routes between them.
//
//   decorative variant  -> Cityscape | Globe   (aria-hidden, never interactive)
//   fill mode           -> square tile          (profile / community Media grids)
//   1 image             -> MediaFrame           (height-budgeted, never cropped)
//   2+ images           -> MediaDeck            (swipeable deck)
//   uploaded video      -> VideoFrame           (a post carries images OR one video)

import type { Post, PostImage, PostMedia as PostMediaType } from '@/core/models/post';
import { isHttpUrl } from '@/core/lib/isHttpUrl';
import { useMediaViewer } from '@/components/MediaViewer';
import { isDecorativeMedia, postImages } from './postImages';
import { Cityscape } from './media/decorative/Cityscape';
import { Globe } from './media/decorative/Globe';
import { MediaFrame } from './media/MediaFrame';
import { MediaDeck } from './media/MediaDeck';
import { VideoFrame } from './media/VideoFrame';

export interface PostMediaProps {
  /** Legacy single-media value: a decorative variant or an image URL. */
  media?: PostMediaType;
  /**
   * Fill the parent container instead of the height-budgeted frame. Used by the profile and
   * community Media grids so every tile is a uniform square. Renders a plain div, never a
   * button — those tiles are already wrapped in a Link.
   */
  fill?: boolean;
  /** Full post, when available — unlocks the multi-image `images` array and `video`. */
  post?: Pick<Post, 'media' | 'images'> & Partial<Pick<Post, 'video'>>;
  /** Post detail gets a larger height budget than the feed. */
  variant?: 'feed' | 'hero';
  /** Eager-load (use for the first post in the feed / the hero). */
  priority?: boolean;
  /** Open the viewer at `index`. */
  onOpen?: (index: number) => void;
}

export function PostMedia({
  media,
  fill = false,
  post,
  variant = 'feed',
  priority = false,
  onOpen,
}: PostMediaProps) {
  const viewer = useMediaViewer();
  const source: Pick<Post, 'media' | 'images'> = post ?? { media };

  if (post?.video) return <VideoFrame video={post.video} fill={fill} variant={variant} />;

  // Decorative ornaments are not photographs: no viewer, no brackets, no cursor change.
  if (isDecorativeMedia(source.media)) {
    if (source.media === 'cityscape') return <Cityscape fill={fill} />;
    if (source.media === 'globe') return <Globe fill={fill} />;
  }

  const items: PostImage[] = postImages(source);
  // Tapping the photo inspects the photo; tapping anywhere else on the card opens the post.
  // (The frame is a <button>, which PostCard's card-click guard already skips.)
  const openAt = (i: number) => (onOpen ? onOpen(i) : viewer.open(items, i));

  if (items.length === 0) {
    // A bare URL passed via `media` with no post context.
    if (isHttpUrl(media)) {
      return (
        <MediaFrame
          item={{ url: media as string, alt: '', width: null, height: null }}
          fill={fill}
          variant={variant}
          priority={priority}
          onOpen={onOpen ? () => onOpen(0) : undefined}
        />
      );
    }
    return null;
  }

  // Media grids show a single representative tile.
  if (fill) {
    const first = items[0];
    return first ? <MediaFrame item={first} fill variant={variant} priority={priority} /> : null;
  }

  if (items.length === 1) {
    const only = items[0];
    return only ? (
      <MediaFrame item={only} variant={variant} priority={priority} onOpen={() => openAt(0)} />
    ) : null;
  }

  return <MediaDeck items={items} variant={variant} priority={priority} onOpen={openAt} />;
}
